import * as React from "react";
import { TreeNode } from "../model/node";
import { NodeActions } from "../model/permissions";
import { attributeKind } from "./attribute";
import { groupKind } from "./group";
import { objectKind } from "./object";
import {
  ChildrenLoader,
  FormDataContext,
  KindContext,
  SaveContext,
  SaveResult,
} from "./types";
import { valueKind } from "./value";

/**
 * Реестр вариантов узла.
 *
 * Наружу торчат только узкие функции ниже. Обращение к обработчикам по ключу
 * живёт исключительно в этом файле: иначе необязательность методов и разбор по
 * `kind` расползутся по коду — ровно то, от чего уходили.
 */
type KindVisitor<R> = {
  object(node: Extract<TreeNode, { kind: "object" }>): R;
  group(node: Extract<TreeNode, { kind: "group" }>): R;
  attribute(node: Extract<TreeNode, { kind: "attribute" }>): R;
  value(node: Extract<TreeNode, { kind: "value" }>): R;
};

const unknownKind = (node: never): never => {
  throw Error(`Неизвестный вариант узла: ${JSON.stringify(node)}`);
};

/**
 * Единственная реализация разбора по варианту. Добавление нового вида узла
 * обрушит компиляцию здесь и в самом реестре — молча пропустить его нельзя.
 *
 * Экспортируется для проекции: она строит по варианту разные DTO. Сама таблица
 * обработчиков наружу не видна, поэтому необязательность методов не течёт.
 */
export const dispatch = <R>(node: TreeNode, visitor: KindVisitor<R>): R => {
  switch (node.kind) {
    case "object":
      return visitor.object(node);
    case "group":
      return visitor.group(node);
    case "attribute":
      return visitor.attribute(node);
    case "value":
      return visitor.value(node);
    default:
      return unknownKind(node);
  }
};

export const nodeIcon = (node: TreeNode): React.ReactNode =>
  dispatch(node, {
    object: (it) => objectKind.icon(it),
    group: (it) => groupKind.icon(it),
    attribute: (it) => attributeKind.icon(it),
    value: (it) => valueKind.icon(it),
  });

export const nodeTitle = (
  node: TreeNode,
  actions: NodeActions,
): React.ReactNode =>
  dispatch(node, {
    object: (it) => objectKind.title(it, actions),
    group: (it) => groupKind.title(it, actions),
    attribute: (it) => attributeKind.title(it, actions),
    value: (it) => valueKind.title(it, actions),
  });

/** Права самого узла, без наследования от владельца. */
export const nodeOwnActions = (node: TreeNode, ctx: KindContext): NodeActions =>
  dispatch(node, {
    object: (it) => objectKind.ownActions(it, ctx),
    group: (it) => groupKind.ownActions(it, ctx),
    attribute: (it) => attributeKind.ownActions(it, ctx),
    value: (it) => valueKind.ownActions(it, ctx),
  });

/** Данные для формы редактирования узла. */
export const nodeFormData = (
  node: TreeNode,
  ctx: FormDataContext,
): Promise<unknown> =>
  dispatch(node, {
    object: (it) => objectKind.formData(it, ctx),
    group: (it) => groupKind.formData(it, ctx),
    attribute: (it) => attributeKind.formData(it, ctx),
    value: (it) => valueKind.formData(it, ctx),
  });

/** Нужна ли узлу стрелка раскрытия. Ответ известен до всякой загрузки. */
export const nodeExpandable = (node: TreeNode): boolean =>
  dispatch(node, {
    object: (it) => objectKind.isExpandable(it),
    group: (it) => groupKind.isExpandable(it),
    attribute: () => false,
    value: (it) => valueKind.isExpandable(it),
  });

/**
 * Загрузчик детей или `null`, если у варианта детей не бывает.
 * Потребитель видит наличие или отсутствие, а не необязательный метод.
 */
export const childrenLoader = (
  node: TreeNode,
  ctx: KindContext,
): ChildrenLoader | null =>
  dispatch<ChildrenLoader | null>(node, {
    object: (it) => () => objectKind.children(it, ctx),
    group: (it) => () => groupKind.children(it, ctx),
    attribute: () => null,
    value: (it) => () => valueKind.children(it, ctx),
  });

/**
 * Текст подтверждения удаления. Есть у любого узла: окно открывается по
 * выбранному узлу, и вопрос должен называть то, что видит пользователь.
 */
export const nodeRemoveMessage = (node: TreeNode): string =>
  dispatch(node, {
    object: (it) => objectKind.removeMessage(it),
    group: (it) => groupKind.removeMessage(it),
    attribute: (it) => attributeKind.removeMessage(it),
    value: (it) => valueKind.removeMessage(it),
  });

/**
 * Удаление узла или `null`, если вариант удалять нельзя.
 * Отделено от текста вопроса: спросить можно про любой узел, выполнить — нет.
 */
export const removeAction = (
  node: TreeNode,
  ctx: KindContext,
): (() => Promise<void>) | null =>
  dispatch<(() => Promise<void>) | null>(node, {
    object: (it) => () => objectKind.remove(it, ctx),
    group: (it) => () => groupKind.remove(it, ctx),
    attribute: (it) => () => attributeKind.remove(it, ctx),
    value: () => null,
  });

/**
 * Сохранение узла или `null`, если вариант сохранять нельзя.
 * Кнопка сохранения не отрисуется там, где сохранять нечего.
 */
export const saveIntent = (
  node: TreeNode,
  ctx: SaveContext,
): ((values: unknown) => Promise<SaveResult>) | null =>
  dispatch<((values: unknown) => Promise<SaveResult>) | null>(node, {
    object: (it) => (values) => objectKind.save(it, values, ctx),
    group: (it) => (values) => groupKind.save(it, values, ctx),
    attribute: (it) => (values) => attributeKind.save(it, values, ctx),
    value: () => null,
  });
